import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import {
  EVENTS,
  sendContactSimilarListingsTrackingEvent,
  useTrackingWithSort,
  VARIANTS
} from '@/api/tracking'
import {
  Box,
  Button,
  IconInfoInverted,
  List,
  Stack,
  Text,
  TextLink,
  TooltipRenderer,
  useDialogController,
  useDialogControllers,
  useResponsiveValue,
  useToast
} from '@machobear/design-system'
import { Formik, Form as FormikForm } from 'formik'
import omit from 'lodash/omit'
import { useDispatch } from 'react-redux'

import config from '@/config'
import { usePublicSession } from '@/models/session/hooks'
import { startPublicChat, tenantStartChat } from '@/ports'
import { colors } from '@/styles'
import Yup from '@/lib/yup'
import useCooldown from '@/hooks/use-cooldown'
import useGoogleEventUserData from '@/hooks/use-google-event-user-data'
import useRole from '@/hooks/use-role'
import useSessionComplete from '@/hooks/use-session-complete'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import { contactSimilarListingFunc } from '@/features/Listings/contact-form/inquiryMessageUtil'
import { useAlerts } from '@/components/alert/useAlerts.tsx'
import { Captcha } from '@/components/captcha/captcha'
import { FieldCheckbox } from '@/components/ds-form/check-box-field'
import DateDropdownField from '@/components/ds-form/date-dropdown-field'
import { FieldInput } from '@/components/ds-form/text-field'
import { TextAreaField } from '@/components/ds-form/textarea-field'
import { WarningModal } from '@/components/ds-warning-modal'
import { Error, Group, Row } from '@/components/form'
import Skeleton from '@/components/skeleton'
import { ParagraphLarge } from '@/components/text'

import { saveCustomMessage } from './inquiryMessageUtil'
import { useListingDetailInquiryMessage } from './useInquiryMessage'

const Wrapper = styled.div`
  padding: 24px;
  border-radius: 8px;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey88};
  box-shadow: 0px 4px 6px -2px rgba(0, 0, 0, 0.06),
    0px 2px 4px -1px rgba(0, 0, 0, 0.04);
`

const Title = styled(ParagraphLarge)`
  color: ${colors.black};
  font-weight: 700;
  margin: 0 0 24px;
`

export const StyledForm = styled(FormikForm)`
  width: 100%;
  label {
    margin-bottom: 0;
  }
`

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
`

const getTrackingClickId = (isLoggedIn, allowApplications, listingId) => {
  if (allowApplications) {
    return isLoggedIn
      ? `logged-inquire-submit-${listingId}`
      : `listing-inquire-submit-${listingId}`
  } else {
    return isLoggedIn
      ? `logged-inquire-blockapp-submit-${listingId}`
      : `listing-inquire-blockapp-submit-${listingId}`
  }
}

export const Terms = () => {
  const { t } = useTranslation()
  return (
    <Text tone="tertiary" size="xsmall">
      <Trans i18nKey="b.listing.view.message_modal.terms" t={t}>
        By proceeding, I confirm that I have read and agree to liv.rent&#39;s{' '}
        <TextLink
          href="https://liv.rent/terms"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: colors.link }}
        >
          Terms & Conditions
        </TextLink>{' '}
        and{' '}
        <TextLink
          href="https://liv.rent/privacy"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: colors.link }}
        >
          Privacy Policy
        </TextLink>
        .
      </Trans>
    </Text>
  )
}

export const ContactFormBody = ({
  prefix,
  listingId,
  isModal,
  allowApplications,
  isRented,
  onError,
  onSuccess
}) => {
  const { t } = useTranslation()
  const showToast = useToast()
  const isLandlord = useRole() === 'landlord'
  const isLoggedIn = useSessionComplete()
  const dispatch = useDispatch()
  const logout = () => dispatch.auth.logout()
  const captchaRef = useRef()
  const { visible, showModal, closeModal } = useDialogController(false)
  const isMobile = useResponsiveValue()({
    desktop: false,
    tablet: false,
    mobile: true
  })

  // formik's isSubmitting is toggled off at the end of submission and makes submit button tempoarily enabled before closing
  // customSubmitting is required to take over that behaviour and keep button disabled until modal is closed
  const [customSubmitting, setCustomSubmitting] = useState(false)

  const inquiryMessageHook = useListingDetailInquiryMessage(isLoggedIn)
  const { message, defaultMessage } = inquiryMessageHook

  useEffect(() => {
    if (isLoggedIn) return
    dispatch.session.loadChatPublicSession()
  }, [dispatch.session, isLoggedIn])

  const publicSession = usePublicSession()

  const { isCooling, startCooldown, timeLeft } = useCooldown()

  const trackingClickId = getTrackingClickId(
    isLoggedIn,
    allowApplications,
    listingId
  )

  const onSubmit = async values => {
    setCustomSubmitting(true)
    if (isLandlord) {
      showModal()
      return
    }

    saveCustomMessage({
      isLoggedIn: isLoggedIn,
      inquiryMessage: message ?? defaultMessage,
      formValues: values
    })

    const contact_similar_listings = values.contact_similar_listings
    values = omit(values, 'contact_similar_listings')
    values.message = values.message.trim()

    // TODO sendInquiryMessage
    const { response, body } = isLoggedIn
      ? await tenantStartChat(values, { listingId })
      : await startPublicChat({ body: values }, { listingId }, undefined, {
          headers: {
            'X-Captcha-Token': values.captcha
          }
        })

    let XTempAccessKey = ''
    if (!isLoggedIn) {
      XTempAccessKey = response.headers.get('X-Temp-Access-Key')
    }

    captchaRef.current?.reset?.()

    if (!response.ok) {
      setCustomSubmitting(false)
      // if user email is already in our system
      if (body.message_key === 'err.public_chat_login_required') {
        onError({
          code: 'err.public_chat_login_required',
          data: { formValues: values, listingId, contact_similar_listings }
        })
        return
      }

      // TODO message_key must be more specific
      if (body.message_key === 'msg.general_error_request') {
        startCooldown(20)
        showToast({
          tone: 'info',
          message:
            body?.message ??
            t(
              'common.rate_limit_error',
              "It looks like you're sending messages a bit too quickly. Please pause for a short moment and try again."
            )
        })
        return
      }

      showToast({
        tone: 'critical',
        message: body?.message
      })
      return
    }

    if (!contact_similar_listings) {
      onSuccess({ formValues: values, contact_similar_listings })
      setCustomSubmitting(false)
      return
    }

    const contactResult = await contactSimilarListingFunc({
      isLoggedIn,
      listingId,
      formValues: values,
      XTempAccessKey
    })
    if (!contactResult.ok) {
      showToast({
        tone: 'critical',
        message:
          contactResult.message ?? 'Unable to contact the similar listings'
      })
    } else {
      onSuccess({ formValues: values, contact_similar_listings })
    }
    setCustomSubmitting(false)
  }

  const initialValues = {
    message: inquiryMessageHook.message?.content,
    move_in_date: 'asap',
    contact_similar_listings: true,
    ...(!isLoggedIn
      ? {
          first_name: publicSession.first_name || '',
          last_name: publicSession.last_name || '',
          email: publicSession.username || '',
          captcha: ''
        }
      : {})
  }

  const validationSchema = Yup.object().shape({
    message: Yup.string().required().max(500),
    move_in_date: Yup.string().required(),
    ...(!isLoggedIn
      ? {
          first_name: Yup.string().required(),
          last_name: Yup.string().required(),
          email: Yup.string().email().required(),
          captcha: Yup.string().test(
            'captcha-required',
            "Captcha is required. Please wait while it's validating.",
            function (value) {
              return !shouldValidateCaptcha || !!value
            }
          )
        }
      : {})
  })

  const shouldValidateCaptcha = config.ENABLE_CONTACT_FORM_CAPTCHA && !isRented
  const bottomGap = 15

  // must add !customSubmitting to prevent the skeleton from showing
  // while submission is stilling going
  if (!customSubmitting && inquiryMessageHook.isLoading) {
    return (
      <>
        <form noValidate style={{ width: '100%' }}>
          <Skeleton height={60} bottom={bottomGap} />
          <Skeleton height={120} bottom={bottomGap} />
          <Button theme="blueSecondary" type="submit" disabled={true}>
            {t('b.appointment.modal.send_message.button', 'Send Message')}
          </Button>
        </form>
      </>
    )
  }

  return (
    <>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          dirty,
          isValid,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          setFieldError
        }) => {
          const contactSimilarListingsCheckbox = (
            <FieldCheckbox
              name="contact_similar_listings"
              label={t('Contact similar listings')}
              checked={values.contact_similar_listings}
              badge={
                <TooltipRenderer
                  tooltip={
                    <Stack space="small">
                      <Text>{t('If checked, liv.rent will')}:</Text>
                      <List>
                        <Text>
                          {t(
                            'Automatically message up to 3 similar listings on your behalf'
                          )}
                        </Text>
                        <Text>
                          {t(
                            'Notify you of similar, newly listed properties via email'
                          )}
                        </Text>
                      </List>
                    </Stack>
                  }
                >
                  {({ triggerProps }) => (
                    <Box
                      display="inline"
                      aria-label="Tooltip"
                      {...triggerProps}
                    >
                      <IconInfoInverted tone="tertiary" />
                    </Box>
                  )}
                </TooltipRenderer>
              }
            />
          )

          if (isLoggedIn) {
            return (
              <StyledForm noValidate>
                <Row>
                  <Group col={12} bottom={bottomGap}>
                    <DateDropdownField
                      name="move_in_date"
                      label={t('Move-in Date')}
                      disabled={isRented}
                      initialValue={initialValues.move_in_date}
                    />
                  </Group>
                </Row>
                <Row>
                  <Group col={12} bottom={bottomGap}>
                    <TextAreaField
                      name="message"
                      label={t('Message')}
                      line={6}
                      required
                      disabled={isRented}
                    />
                  </Group>
                </Row>
                <Row>
                  <Group col={12} bottom={bottomGap}>
                    {contactSimilarListingsCheckbox}
                  </Group>
                </Row>
                <Box>
                  <Button
                    theme="blueSecondary"
                    type="submit"
                    id={trackingClickId}
                    disabled={
                      customSubmitting ||
                      (dirty && !isValid) ||
                      isRented ||
                      isCooling
                    }
                  >
                    {customSubmitting
                      ? t('Submitting...')
                      : isCooling
                      ? t(
                          'common.try_again_cooldown',
                          'Try again ({{cooldownSeconds}}s)',
                          { cooldownSeconds: timeLeft }
                        )
                      : t(
                          'b.appointment.modal.send_message.button',
                          'Send Message'
                        )}
                  </Button>
                </Box>
              </StyledForm>
            )
          }

          return (
            <StyledForm noValidate>
              <Row>
                <Group col={isMobile ? 12 : 6} bottom={bottomGap}>
                  <FieldInput
                    label={t('common.first_name', 'First name')}
                    name="first_name"
                    disabled={isRented}
                    autoComplete="section-contact-form given-name"
                    prefix={prefix}
                    required
                  />
                </Group>
                <Group col={isMobile ? 12 : 6} bottom={bottomGap}>
                  <FieldInput
                    label={t('common.last_name', 'Last name')}
                    name="last_name"
                    disabled={isRented}
                    autoComplete="section-contact-form family-name"
                    prefix={prefix}
                    required
                  />
                </Group>
              </Row>
              <Row>
                <Group col={12} bottom={bottomGap}>
                  <FieldInput
                    label={t('common.email', 'Email')}
                    name="email"
                    disabled={isRented}
                    autoComplete="section-contact-form username email"
                    type="email"
                    prefix={prefix}
                    required
                  />
                </Group>
                <Group col={12} bottom={bottomGap}>
                  <DateDropdownField
                    name="move_in_date"
                    label={t('Move-in Date')}
                    disabled={isRented}
                    initialValue={initialValues.move_in_date}
                  />
                </Group>
              </Row>
              <Row>
                <Group col={12} bottom={bottomGap}>
                  <TextAreaField
                    name="message"
                    label={t('Message')}
                    lines={6}
                    required
                    disabled={isRented}
                  />
                </Group>
              </Row>
              {shouldValidateCaptcha && (
                <Row>
                  <Group col={12} bottom={bottomGap}>
                    <Captcha
                      id="listing-detail-contact-form"
                      ref={captchaRef}
                      onSuccess={captcha => setFieldValue('captcha', captcha)}
                      onExpire={() => {
                        return setFieldValue('captcha', '')
                      }}
                      onError={() => {
                        setFieldValue('captcha', '', false)
                        setFieldTouched('captcha', true, false)
                        setFieldError(
                          'captcha',
                          t('Captcha validation failed. Please try again.')
                        )
                      }}
                      options={{
                        action:
                          'listing-detail-contact-form' + isModal
                            ? '-modal'
                            : ''
                      }}
                    />
                    <Error name="captcha" />
                  </Group>
                </Row>
              )}
              <Row>
                <Group col={12} bottom={bottomGap}>
                  {contactSimilarListingsCheckbox}
                </Group>
              </Row>
              <Box>
                <Button
                  theme="blueSecondary"
                  type="submit"
                  id={trackingClickId}
                  disabled={
                    isSubmitting || !dirty || (dirty && !isValid) || isRented
                  }
                >
                  {isSubmitting
                    ? t('Submitting...')
                    : t(
                        'b.appointment.modal.send_message.button',
                        'Send Message'
                      )}
                </Button>
              </Box>
            </StyledForm>
          )
        }}
      </Formik>
      <WarningModal
        visible={visible}
        closeModal={closeModal}
        title={t(
          'b.listing.view.logged_in_as_landlord.title',
          "Oops! It looks like you're logged in as a landlord."
        )}
        description={
          <Box marginTop="small">
            <Text tone="secondary" align="center" size="medium">
              <Trans i18nKey="b.listing.view.logged_in_as_landlord.text">
                To access renter-specific features, please log out and sign up
                for a renter account or log in with your renter account details.
                <br />
                <br />
                If you made this account in error, you can contact us at{' '}
                <TextLink
                  href="mailto:support@liv.rent"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  support@liv.rent
                </TextLink>
                .
              </Trans>
            </Text>
          </Box>
        }
        button={{
          text: t('common.log_out', 'Log Out'),
          onClick: () => {
            logout()
            closeModal()
          }
        }}
      />
    </>
  )
}

export const ContactForm = ({
  listingId,
  listing,
  isRented,
  allowApplications,
  ...rest
}) => {
  const { t } = useTranslation()
  const { sendAlert } = useAlerts()
  const { visible, showModal, closeModal } = useDialogControllers({
    login: false,
    registerAlert: false
  })
  const [inquiryData, setInquiryData] = useState()
  const isLoggedIn = useSessionComplete()
  const { sendInquiryMessageAfterLogin, showInquiryMessageToast } =
    useListingDetailInquiryMessage(isLoggedIn)

  const userData = useGoogleEventUserData()
  const { mutateAsync: track } = useTrackingWithSort()

  const sendTracking = useCallback(
    (formValues, contact_similar_listings) => {
      track({
        event_name: isLoggedIn
          ? EVENTS.INQUIRY_SENT
          : EVENTS.PUBLIC_CHAT_STARTED,
        metadata: { listingId, source: 'listing-detail' },
        variant_group: VARIANTS.LISTING_SORT,
        referer: document.referrer
      })
      const eventData = isLoggedIn
        ? {
            event: 'logged-inquire-submit-sent',
            ...userData
          }
        : {
            event: 'listing-inquire-submit-sent',
            userEmail: formValues.email,
            userFirstName: formValues.first_name,
            userLastName: formValues.last_name
          }
      window.dataLayer.push(eventData)

      sendContactSimilarListingsTrackingEvent({
        isChecked: contact_similar_listings,
        isDetailsPage: true
      })
    },
    [isLoggedIn, listingId, track, userData]
  )

  // TODO try removing useCallback
  const afterLoginFn = useCallback(
    async inquiryData => {
      await sendInquiryMessageAfterLogin(async () => {
        if (inquiryData?.contact_similar_listings) {
          await sendAlert(listing)
        }
        showInquiryMessageToast()
        sendTracking(
          {
            email: inquiryData?.formValues?.email,
            first_name: inquiryData?.formValues?.first_name,
            last_name: inquiryData?.formValues?.last_name
          },
          inquiryData?.contact_similar_listings
        )
      }, inquiryData)
    },
    [
      sendTracking,
      listing,
      sendAlert,
      sendInquiryMessageAfterLogin,
      showInquiryMessageToast
    ]
  )

  return (
    <>
      <Wrapper {...rest}>
        <FormWrapper>
          <Title>
            {t('b.listing.view.message_modal.title', 'Check Availability')}
          </Title>
          <ContactFormBody
            listing={listing}
            listingId={listingId}
            isRented={isRented}
            allowApplications={allowApplications}
            onSuccess={async ({ formValues, contact_similar_listings }) => {
              if (isLoggedIn) {
                if (contact_similar_listings) {
                  await sendAlert(listing)
                }
                showInquiryMessageToast()
              } else {
                await showInquiryMessageToast({
                  listing,
                  formValues,
                  contact_similar_listings
                })
              }
              sendTracking(formValues, contact_similar_listings)
            }}
            onError={({ code, data }) => {
              if (code === 'err.public_chat_login_required') {
                setInquiryData(data)
                showModal('login')()
              }
            }}
          />
          <Box marginTop="small">
            <Terms />
          </Box>
        </FormWrapper>
        <DSLoginModal
          t={t}
          mode="login"
          visible={visible.login}
          onClose={() => {
            closeModal('login')()
          }}
          eventId="web-login-submit-inquire-sendmessage"
          listingId={parseInt(listingId)}
          data={{
            username: inquiryData?.formValues?.email,
            first_name: inquiryData?.formValues?.first_name,
            last_name: inquiryData?.formValues?.last_name
          }}
          afterLogin={() => afterLoginFn(inquiryData)}
        />
      </Wrapper>
    </>
  )
}
